import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "@/components/ui/modal";
import { UnitTableCategoryFilter } from "@/components/controls/filter/UnitTableCategoryFilter";
import { UnitTableFeatureFilter } from "@/components/controls/filter/UnitTableFeatureFilter";
import { UnitTableFacilityFilter } from "@/components/controls/filter/UnitTableFacilityFilter";
import { UnitTableFeaturedFilter } from "@/components/controls/filter/UnitTableFeaturedFilter";
import { Button } from "@/components/ui/button";
import { useFacilities } from "@/hooks/useFacilities";
import type { UnitFeature, UnitCategory } from "@/types";
import { SortOptions } from "@/types/enums";
import {
    selectSelectedFeatures,
    selectSelectedCategories,
    selectSelectedFacilities,
    selectSelectedSort,
    selectSelectedFeatured,
    setSelectedFeatures,
    setSelectedCategories,
    setSelectedFacilities,
    setSelectedSort,
    setSelectedFeatured,
} from "@/features/filters/filtersSlice";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { MoreVertical } from "lucide-react";

interface MoreFiltersButtonProps {
    features: UnitFeature[];
    categories: UnitCategory[];
}

type SortOption = {
    value: SortOptions;
    label: string;
};

const sortOptions: SortOption[] = [
    { value: SortOptions.VACANCY_ASCENDING, label: "Availability: Low - High" },
    {
        value: SortOptions.VACANCY_DESCENDING,
        label: "Availability: High - Low",
    },
    { value: SortOptions.PRICE_ASCENDING, label: "Price: Low - High" },
    { value: SortOptions.PRICE_DESCENDING, label: "Price: High - Low" },
    { value: SortOptions.AREA_ASCENDING, label: "Size: Small - Large" },
    { value: SortOptions.AREA_DESCENDING, label: "Size: Large - Small" },
];

export const MoreFiltersButton = ({
    features,
    categories,
}: MoreFiltersButtonProps) => {
    const dispatch = useDispatch();
    const { facilities } = useFacilities();

    const appliedFeatures = useSelector(selectSelectedFeatures);
    const appliedCategories = useSelector(selectSelectedCategories);
    const appliedFacilities = useSelector(selectSelectedFacilities);
    const appliedSort = useSelector(selectSelectedSort);
    const appliedFeatured = useSelector(selectSelectedFeatured);

    const [draftFeatures, setDraftFeatures] = useState<number[]>([]);
    const [draftCategories, setDraftCategories] = useState<number[]>([]);
    const [draftFacilities, setDraftFacilities] = useState<number[]>([]);
    const [draftSort, setDraftSort] = useState<string>("");
    const [draftFeatured, setDraftFeatured] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setDraftFeatures(appliedFeatures);
            setDraftCategories(appliedCategories);
            setDraftFacilities(appliedFacilities);
            setDraftSort(appliedSort);
            setDraftFeatured(appliedFeatured);
        }
    }, [
        isOpen,
        appliedFeatures,
        appliedCategories,
        appliedFacilities,
        appliedSort,
        appliedFeatured,
    ]);

    const handleClearAll = () => {
        dispatch(setSelectedFeatures([]));
        dispatch(setSelectedCategories([]));
        dispatch(setSelectedFacilities([]));
        dispatch(setSelectedSort(""));
        dispatch(setSelectedFeatured(false));
        setIsOpen(false);
    };

    const handleApply = () => {
        dispatch(setSelectedFeatures(draftFeatures));
        dispatch(setSelectedCategories(draftCategories));
        dispatch(setSelectedFacilities(draftFacilities));
        dispatch(setSelectedSort(draftSort));
        dispatch(setSelectedFeatured(draftFeatured));
        setIsOpen(false);
    };

    const filterCount =
        appliedFeatures.length +
        appliedCategories.length +
        appliedFacilities.length +
        (appliedSort ? 1 : 0) +
        (appliedFeatured ? 1 : 0);

    return (
        <>
            <button
                type="button"
                className="more-filters-button"
                onClick={() => setIsOpen(true)}
            >
                <span className="more-filters-button__text">
                    More
                    {filterCount > 0 ? (
                        <span className="more-filters-button__badge">
                            {filterCount}
                        </span>
                    ) : null}
                </span>
                <span
                    className="more-filters-button__divider"
                    aria-hidden="true"
                />
                <MoreVertical
                    className="more-filters-button__menu-icon"
                    size={20}
                    strokeWidth={2.25}
                    aria-hidden="true"
                />
            </button>
            <Modal
                open={isOpen}
                onClose={() => setIsOpen(false)}
                className="se-unit-table se-unit-table-filters"
            >
                <h4 className="tw:font-bold tw:mb-1">Filters & Sort</h4>

                <UnitTableFacilityFilter
                    facilities={facilities}
                    selectedFacilities={draftFacilities}
                    onSelectionChange={setDraftFacilities}
                />
                <UnitTableFeatureFilter
                    features={features}
                    selectedFeatures={draftFeatures}
                    onSelectionChange={setDraftFeatures}
                />
                <UnitTableCategoryFilter
                    categories={categories}
                    selectedCategories={draftCategories}
                    onSelectionChange={setDraftCategories}
                />
                <UnitTableFeaturedFilter
                    selectedFeatured={draftFeatured}
                    onSelectionChange={setDraftFeatured}
                />

                <div className="tw:mt-4">
                    <p className="tw:font-bold">Sort By</p>
                    <RadioGroup
                        value={draftSort}
                        onValueChange={setDraftSort}
                        className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-4"
                    >
                        {sortOptions.map(({ value, label }) => {
                            const inputId = `sort-${value.replace(/[^a-z0-9]+/gi, "-")}`;
                            return (
                                <div
                                    key={value}
                                    className="tw:items-start tw:flex tw:flex-row tw:space-x-3 tw:space-y-0 tw:py-1"
                                >
                                    <RadioGroupItem
                                        value={value}
                                        id={inputId}
                                        className="tw:mt-0.5"
                                    />
                                    <Label
                                        htmlFor={inputId}
                                        className="tw:text-sm tw:font-normal tw:ml-2 tw:cursor-pointer tw:no-underline tw:text-foreground"
                                    >
                                        {label}
                                    </Label>
                                </div>
                            );
                        })}
                    </RadioGroup>
                </div>

                <div className="tw:flex tw:justify-between tw:items-center tw:mt-4">
                    <Button
                        className="btn btn-outline-primary"
                        onClick={handleClearAll}
                    >
                        Clear All
                    </Button>
                    <Button
                        className="btn btn-primary"
                        onClick={handleApply}
                    >
                        Apply
                    </Button>
                </div>
            </Modal>
        </>
    );
};
